import {untrack} from 'svelte';
import {
    addDay, addDuration, bgEvent, cloneDate, createSlots, createSlotTimeLimits, datesEqual, empty, getPayload,
    getWeekNumber, length, outsideRange, toSeconds, toTime
} from '#lib';
import {createChunks, eventInRow, prepareChunks} from './lib.js';

export function grid(mainState, viewState) {
    return () => {
        // Dependencies
        let {viewDates, options: {highlightedDates, validRange}} = mainState;
        let {dayTimeLimits, viewResources} = viewState;

        let grid = [];

        untrack(() => {
            let gridRow = 1
            for (let resource of viewResources) {
                let days = [];
                let gridColumn = 1;
                for (let date of viewDates) {
                    let slotTimeLimits = dayTimeLimits[toTime(date)];
                    days.push({
                        gridColumn,
                        gridRow,
                        resource,
                        start: addDuration(cloneDate(date), slotTimeLimits.min),
                        end: addDuration(cloneDate(date), slotTimeLimits.max),
                        dayStart: date,
                        dayEnd: addDay(cloneDate(date)),
                        disabled: outsideRange(date, validRange),
                        highlight: highlightedDates.some(d => datesEqual(d, date))
                    });
                    ++gridColumn;
                }
                grid.push(days);
                ++gridRow;
            }
        });

        return grid;
    };
}

/**
 * Check whether the view spans exactly one whole month
 */
function oneMonth(duration) {
    return duration.months === 1 && !duration.years && !duration.days && !duration.seconds;
}

/**
 * Check whether the view spans exactly one whole week
 */
function oneWeek(duration) {
    return duration.inWeeks && duration.days === 7 && !duration.years && !duration.months && !duration.seconds;
}

export function extraHeads(mainState, viewState) {
    return () => {
        // Dependencies
        let {features, options: {duration, firstDay, weekNumbers}} = mainState;
        let {grid} = viewState;

        let months = [];
        let weeks = [];

        untrack(() => {
            let month;
            let week;
            if (!empty(grid)) {
                for (let {dayStart, gridColumn} of grid[0]) {
                    if (features.includes('month')) {
                        if (month && month.date.getUTCMonth() === dayStart.getUTCMonth()) {
                            ++month.span;
                        } else {
                            month = {date: dayStart, gridColumn, span: 1};
                            months.push(month);
                        }
                    }
                    if (weekNumbers) {
                        let number = getWeekNumber(dayStart, firstDay);
                        if (week && week.number === number) {
                            ++week.span;
                        } else {
                            week = {number, date: dayStart, gridColumn, span: 1};
                            weeks.push(week);
                        }
                    }
                }
            }
            // A single header is redundant when the view spans exactly one whole month or week
            if (length(months) === 1 && oneMonth(duration)) {
                months = [];
            }
            if (length(weeks) === 1 && oneWeek(duration)) {
                weeks = [];
            }
        });

        return {months, weeks};
    };
}

export function eventChunks(mainState, viewState) {
    return () => {
        // Dependencies
        let {filteredEvents} = mainState;
        let {grid, monthView} = viewState;

        let chunks = [];
        let bgChunks = [];
        let units;

        untrack(() => {
            for (let event of filteredEvents) {
                let bg = bgEvent(event.display);
                if (bg && monthView && !event.allDay) {
                    continue;
                }
                let target = bg ? bgChunks : chunks;
                for (let days of grid) {
                    // The event can only have chunks in the rows of its own resources
                    if (!eventInRow(event, days)) {
                        continue;
                    }
                    let chunk = createChunks(event, days, monthView);
                    if (chunk) {
                        target.push(chunk);
                    }
                }
            }
            units = prepareChunks(chunks, monthView);
        });

        return {chunks, bgChunks, units};
    };
}

export function iEventChunks(mainState, viewState) {
    return () => {
        // Dependencies
        let {iEvents} = mainState;
        let {grid, monthView} = viewState;

        let iChunks = [];

        for (let [, event] of iEvents) {
            if (!event) {
                continue;
            }
            untrack(() => {
                for (let days of grid) {
                    if (!eventInRow(event, days)) {
                        continue;
                    }
                    let chunk = createChunks(event, days, monthView, false);
                    if (chunk) {
                        iChunks.push(chunk);
                    }
                }
            });
        }

        return iChunks;
    };
}

// slotTimeLimits per day
export function dayTimeLimits(mainState) {
    return () => {
        // Dependencies
        let {filteredEvents, viewDates, options: {flexibleSlotTimeLimits, slotMinTime, slotMaxTime}} = mainState;

        let dayTimeLimits = {};

        untrack(() => {
            for (let date of viewDates) {
                dayTimeLimits[toTime(date)] = createSlotTimeLimits(
                    slotMinTime,
                    slotMaxTime,
                    flexibleSlotTimeLimits,
                    [date],
                    filteredEvents
                );
            }
        });

        return dayTimeLimits;
    };
}

export function daySlots(mainState, viewState) {
    return () => {
        // Dependencies
        let {viewDates, options: {slotDuration}} = mainState;
        let {dayTimeLimits, intlSlotLabel, slotLabelPeriodicity} = viewState;

        let slots = {};

        untrack(() => {
            for (let date of viewDates) {
                let key = toTime(date);
                slots[key] = key in dayTimeLimits
                    ? createSlots(date, slotDuration, slotLabelPeriodicity, dayTimeLimits[key], intlSlotLabel)
                    : [];
            }
        });

        return slots;
    };
}

export function nestedResources(mainState) {
    return () => {
        // Dependencies
        let {resources} = mainState;

        let nested;

        untrack(() => {
            nested = resources.some(resource => getPayload(resource).children.length);
        });

        return nested;
    };
}

export function monthView(mainState) {
    return () => {
        // Dependencies
        let {options: {slotDuration}} = mainState;

        let monthView;

        untrack(() => {
            monthView = !toSeconds(slotDuration);
        });

        return monthView;
    };
}
